import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Offcanvas,
  Button,
  Placeholder,
  InputGroup,
} from "react-bootstrap";
import { Filter, Search } from "lucide-react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../FireBase";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../css/RoomListPage.css";

const PRICE_RANGES = [
  { key: "all", label: "All Prices" },
  { key: "0-2000", label: "Up to ₹2,000" },
  { key: "2000-5000", label: "₹2,000–₹5,000" },
  { key: "5000+", label: "Above ₹5,000" },
];

export default function CabRentalListPage() {
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState({});
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [priceRange, setPriceRange] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  // Responsive listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch data
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, "outstation_rentals"), orderBy("from"));
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPackages(data);
        setFiltered(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let tmp = [...packages];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      tmp = tmp.filter(
        (p) =>
          p.from?.toLowerCase().includes(term) ||
          p.to?.toLowerCase().includes(term)
      );
    }

    // Price filter
    if (priceRange !== "all") {
      tmp = tmp.filter((p) => {
        const prices = p.vehiclePrices
          ? Object.values(p.vehiclePrices).map(Number)
          : [];
        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;

        if (priceRange === "0-2000") return minPrice <= 2000;
        if (priceRange === "2000-5000") return minPrice >= 2000 && minPrice <= 5000;
        if (priceRange === "5000+") return minPrice >= 5000;
        return true;
      });
    }

    setFiltered(tmp);
  }, [priceRange, searchTerm, packages]);

  const renderFilters = () => (
    <>
      <h5 className="mb-2">Search</h5>
         <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by location (From or To)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
             <h5 className="mb-2">Filter by Price</h5>
      <Form.Select
        value={priceRange}
        onChange={(e) => {
          setPriceRange(e.target.value);
          setShowOffcanvas(false);
        }}
      >
        {PRICE_RANGES.map((r) => (
          <option key={r.key} value={r.key}>
            {r.label}
          </option>
        ))}
      </Form.Select>
    </>
  );

  return (
    <Container fluid className="py-4" style={{ marginBottom: 60 }}>
      <Row>
        {/* Sidebar (Desktop) */}
        <Col md={3} className="d-none d-md-block" style={{ position: "sticky", top: 20 }}>
          {renderFilters()}
        </Col>

        {/* Main Section */}
        <Col xs={12} md={9}>
          {/* Search bar */}
       

          {/* Loading skeletons */}
          {loading ? (
            <Row className="g-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Col key={i} xs={12} md={6}>
                  <Card className="shadow-sm rounded overflow-hidden room-card">
                    <div
                      className="placeholder-glow"
                      style={{ height: 200, backgroundColor: "#e9ecef" }}
                    >
                      <Placeholder xs={12} animation="glow" />
                    </div>
                    <Card.Body>
                      <Placeholder animation="glow">
                        <Placeholder as={Card.Title} xs={8} />
                        <Placeholder xs={6} className="mt-2" />
                      </Placeholder>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              {/* Mobile Filter Button */}
              {isMobile && (
                <Button
                  className="d-md-none btn-own"
                  onClick={() => setShowOffcanvas(true)}
                  style={{
                    position: "fixed",
                    bottom: 80,
                    right: 20,
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    zIndex: 1000,
                  }}
                >
                  <Filter size={24} />
                </Button>
              )}

              {/* Cab Cards */}
              <Row className="g-4">
                {filtered.map((pkg) => {
                  const cover =
                    Array.isArray(pkg.images) && pkg.images.length > 0 ? pkg.images[0] : "";
                  const loaded = imgLoaded[pkg.id] === true;
                  const prices = pkg.vehiclePrices
                    ? Object.values(pkg.vehiclePrices).map(Number)
                    : [];
                  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;

                  return (
                    <Col key={pkg.id} xs={12} md={6}>
                      <Card
                        className="shadow-sm rounded overflow-hidden room-card"
                        onClick={() => navigate(`/cab-rental-details/${pkg.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="position-relative">
                          {!loaded && (
                            <div
                              className="placeholder-glow"
                              style={{ height: 200, backgroundColor: "#e9ecef" }}
                            />
                          )}
                          {cover && (
                            <Card.Img
                              variant="top"
                              src={cover}
                              onLoad={() =>
                                setImgLoaded((prev) => ({ ...prev, [pkg.id]: true }))
                              }
                              style={{
                                height: 200,
                                objectFit: "cover",
                                display: loaded ? "block" : "none",
                              }}
                              alt={`${pkg.from} to ${pkg.to}`}
                            />
                          )}
                        </div>

                        <Card.Body>
                          {!loaded ? (
                            <>
                              <Skeleton width={200} />
                              <Skeleton width={120} className="mt-2" />
                            </>
                          ) : (
                            <>
                              <div className="fw-bold mb-1" style={{ color: "#0f172a" }}>
                                {pkg.from} → {pkg.to}
                              </div>
                              <div className="fw-bold mb-1" style={{ color: "#54b435" }}>
                                ₹{minPrice.toLocaleString("en-IN")}{" "}
                                <small className="text-muted ms-1">Onwards</small>
                              </div>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}

                {filtered.length === 0 && !loading && (
                  <Col xs={12}>
                    <div className="text-center text-muted py-5">
                      No cab rentals found.
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Col>
      </Row>

      {/* Mobile Offcanvas */}
      <Offcanvas
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filter Cab Rentals</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>{renderFilters()}</Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
